// Shared phone-display helpers for the two onboarding handoff stages:
//   - agent SPA's OnboardingSheet  (src/web/)
//   - marketing landing's MessageOriModal (src/marketing/)
//
// These format Ori's outbound number for display and build the SMS
// deep-link used by the "Open Messages" handoff CTA. (Phone-input
// helpers were removed when onboarding stopped collecting a phone
// number.)

export function formatPhone(e164: string): string {
  const us = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  if (us) return `+1 (${us[1]}) ${us[2]}-${us[3]}`;
  const cn = /^\+86(\d{3})(\d{4})(\d{4})$/.exec(e164);
  if (cn) return `+86 ${cn[1]} ${cn[2]} ${cn[3]}`;
  return e164;
}

// Sanitize a messaging handle for an `sms:` deep link. Phone numbers are
// stripped to E.164 (`+` and digits) so Android's strict `sms:` parser accepts
// them — iOS tolerates spaces / parens / dashes, Android does not. An Apple-ID
// EMAIL handle (a group member reached over iMessage can be an Apple ID, not a
// phone) is left intact — stripping it to digits would destroy it. Mirrors the
// server-side resolveOriOutgoingHandle() in edge messaging/reply-deeplink.js.
export function sanitizeSmsHandle(handle: string): string {
  const h = (handle || '').trim();
  if (!h) return '';
  if (h.includes('@')) return h; // Apple ID email — not a phone number
  return h.replace(/[^+0-9]/g, '');
}

// Pull the bare handle (phone / email) back out of an sms:/mailto:/wa.me deep
// link, for the copy-the-number fallback. Strips the scheme and any `?`/`&`
// body query. Returns '' if there's nothing usable.
export function parseHandleFromHref(href: string): string {
  const h = (href || '').trim();
  if (!h) return '';
  const stripped = h
    .replace(/^sms:/i, '')
    .replace(/^mailto:/i, '')
    .replace(/^https?:\/\/wa\.me\//i, '')
    .replace(/^whatsapp:\/\/send\/?/i, '');
  return stripped.split(/[?&]/)[0].trim();
}

// SMS deep-link for the handoff stage. iOS uses `&body=`; Android uses
// `?body=`. UA sniff is the only reliable cross-platform path here; the
// underlying sms: scheme is the same. Empty body → no pre-filled text,
// used by the returning-user handoff where the user already knows Ori.
// The number is sanitized so the URI is valid on Android (see above).
export function buildSmsHref(toNumber: string, body: string): string {
  const to = sanitizeSmsHandle(toNumber);
  if (!body) return `sms:${to}`;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const sep = /iPhone|iPad|iPod|Mac/i.test(ua) ? '&body=' : '?body=';
  return `sms:${to}${sep}${encodeURIComponent(body)}`;
}
